import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Calendar, ArrowLeft } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string | null;
  content: string;
  image_url: string | null;
  created_at: string;
}

const BlogPostPage = () => {
  const { id } = useParams<{ id: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      if (!id) return;
      
      const { data } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', id)
        .eq('published', true)
        .single();
      
      setPost(data);
      setLoading(false);
    };

    fetchPost();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream">
        <Header />
        <main className="pt-24 pb-20">
          <div className="container mx-auto px-4">
            <p className="text-charcoal/60 text-center">Laster...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-cream">
        <Header />
        <main className="pt-24 pb-20">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-2xl font-serif text-charcoal mb-4">Innlegg ikke funnet</h1>
            <Link to="/#aktuelt" className="text-coral hover:underline inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Tilbake til aktuelt
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <Header />
      <main className="pt-24 pb-20">
        <article className="container mx-auto px-4 max-w-3xl">
          <Link to="/#aktuelt" className="text-coral hover:underline inline-flex items-center gap-2 mb-8">
            <ArrowLeft className="w-4 h-4" />
            Tilbake til aktuelt
          </Link>
          
          {post.image_url && (
            <div className="aspect-video overflow-hidden rounded-lg mb-8">
              <img
                src={post.image_url}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          <div className="flex items-center gap-2 text-sm text-charcoal/60 mb-4">
            <Calendar className="w-4 h-4" />
            {new Date(post.created_at).toLocaleDateString('nb-NO', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
          </div>
          
          <h1 className="text-3xl md:text-4xl font-serif text-charcoal mb-8">
            {post.title}
          </h1>
          
          <div className="prose prose-lg max-w-none text-charcoal/80 whitespace-pre-wrap">
            {post.content}
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPostPage;
